<template>
  <Teleport to="body">
    <Transition name="modal-fade">
      <div v-if="show" class="mdl-overlay" @click.self="$emit('close')" tabindex="-1" @keydown.esc="$emit('close')">
        <div class="mdl-card shadow-2xl modal-forfeit-card" role="dialog" aria-modal="true">
          <!-- Top Accent Bar -->
          <div class="modal-accent-bar"></div>

          <!-- Header -->
          <div class="modal-header-section d-flex align-items-center justify-content-between p-4 pb-3">
            <div class="d-flex align-items-center gap-3">
              <div class="modal-badge-icon">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M16 3h5v5"/>
                  <path d="M4 20L21 3"/>
                  <path d="M21 16v5h-5"/>
                  <path d="M15 15l6 6"/>
                  <path d="M4 4l5 5"/>
                </svg>
              </div>
              <div>
                <h4 class="modal-title mb-0 fw-bold">E-mail cím átadása a szülőnek</h4>
                <p class="modal-subtitle mb-0 small">
                  {{ childName ? childName + ' fiókadatai' : 'Táncos fiók' }}
                </p>
              </div>
            </div>
            <button
              type="button"
              class="modal-close-button"
              @click="$emit('close')"
              aria-label="Bezárás"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"/>
                <line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
          </div>

          <!-- Body -->
          <div class="modal-body-section px-4 pb-4">
            <!-- Transfer Flow Visual -->
            <div class="transfer-flow-card p-3 rounded-4 mb-3.5">
              <div class="d-flex align-items-center justify-content-between text-xs fw-semibold mb-2 transfer-flow-labels">
                <span class="d-inline-flex align-items-center gap-1">
                  <span class="role-dot role-dot-dancer"></span> Jelenlegi táncos e-mail
                </span>
                <span class="transfer-arrow">➔</span>
                <span class="d-inline-flex align-items-center gap-1">
                  <span class="role-dot role-dot-parent"></span> Új szülői e-mail
                </span>
              </div>
              <div class="transfer-email-display d-flex align-items-center gap-2 p-2.5 rounded-3">
                <div class="transfer-mail-icon d-flex align-items-center">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                    <polyline points="22,6 12,13 2,6"/>
                  </svg>
                </div>
                <div class="transfer-mail-text fw-bold flex-grow-1">
                  {{ currentDancerEmail || 'Nincs megadva' }}
                </div>
                <span class="badge transfer-badge rounded-pill px-2.5 py-1">Átrendelésre kerül</span>
              </div>
              <p class="transfer-explainer mt-2 mb-0 small">
                Ez az e-mail cím ezentúl a gondviselő hivatalos kapcsolattartási fiókjához fog tartozni. A szülő ezzel a címmel fog tudni belépni, kezelni a táncos óráit és fogadni az értesítéseket.
              </p>
            </div>

            <form @submit.prevent="handleConfirm">
              <!-- New Email for Dancer (Optional) -->
              <div class="mb-3">
                <div class="d-flex align-items-center justify-content-between mb-1.5">
                  <label class="form-label small fw-semibold mb-0 input-label">
                    Új e-mail cím a táncosnak
                  </label>
                  <span class="badge optional-badge rounded-pill px-2 py-0.5">Opcionális</span>
                </div>
                <div class="input-wrapper">
                  <span class="input-icon d-flex align-items-center">
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                      <circle cx="12" cy="7" r="4"/>
                    </svg>
                  </span>
                  <input
                    type="email"
                    v-model="newDancerEmail"
                    class="form-control modern-input-styled"
                    placeholder="pl. tancos.sajat@email.hu"
                    :disabled="isSubmitting"
                  />
                </div>
                
                <!-- Dynamic Helper Cue -->
                <div class="helper-box mt-2 p-2.5 rounded-3 small" :class="newDancerEmail.trim() ? 'helper-box--active' : 'helper-box--default'">
                  <div v-if="newDancerEmail.trim()" class="d-flex align-items-start gap-2">
                    <span class="helper-icon d-flex align-items-center mt-0.5">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                        <polyline points="22,6 12,13 2,6"/>
                      </svg>
                    </span>
                    <div>
                      A táncos erre az új címre kap aktivációs levelet, amellyel létrehozhatja saját belépési jelszavát.
                    </div>
                  </div>
                  <div v-else class="d-flex align-items-start gap-2">
                    <span class="helper-icon d-flex align-items-center mt-0.5">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <circle cx="12" cy="12" r="10"/>
                        <line x1="12" y1="16" x2="12" y2="12"/>
                        <line x1="12" y1="8" x2="12.01" y2="8"/>
                      </svg>
                    </span>
                    <div>
                      Ha üresen hagyod, a táncosnak <strong>nem lesz külön e-mail címe</strong>. Minden értesítés és fizetési igazolás kizárólag a szülőnek érkezik.
                    </div>
                  </div>
                </div>
              </div>

              <!-- Error Alert -->
              <div v-if="displayError" class="alert-error-box p-3 rounded-3 small mb-3 fw-semibold d-flex align-items-center gap-2">
                <span class="error-icon d-flex align-items-center">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                    <line x1="12" y1="9" x2="12" y2="13"/>
                    <line x1="12" y1="17" x2="12.01" y2="17"/>
                  </svg>
                </span>
                <div>{{ displayError }}</div>
              </div>

              <!-- Actions -->
              <div class="modal-footer-section d-flex align-items-center justify-content-end gap-2 pt-3">
                <button
                  type="button"
                  class="btn modal-cancel-btn btn-sm px-3.5 py-2 rounded-3"
                  @click="$emit('close')"
                  :disabled="isSubmitting"
                >
                  Mégse
                </button>
                <button
                  type="submit"
                  class="modern-btn modern-btn--primary btn-sm px-4 py-2 rounded-3 d-inline-flex align-items-center gap-2"
                  :disabled="isSubmitting"
                >
                  <span v-if="isSubmitting" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                  <span v-if="isSubmitting">Feldolgozás...</span>
                  <span v-else class="d-inline-flex align-items-center gap-1.5">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <path d="M17 1l4 4-4 4"/>
                      <path d="M3 11V9a4 4 0 0 1 4-4h14"/>
                      <path d="M7 23l-4-4 4-4"/>
                      <path d="M21 13v2a4 4 0 0 1-4 4H3"/>
                    </svg>
                    E-mail átadása a szülőnek
                  </span>
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script>
export default {
  name: 'ChildForfeitEmailModal',
  props: {
    show: {
      type: Boolean,
      default: false
    },
    currentDancerEmail: {
      type: String,
      default: ''
    },
    childName: {
      type: String,
      default: ''
    },
    isSubmitting: {
      type: Boolean,
      default: false
    },
    error: {
      type: String,
      default: ''
    }
  },
  emits: ['close', 'confirm'],
  data() {
    return {
      newDancerEmail: '',
      localError: ''
    };
  },
  computed: {
    displayError() {
      return this.localError || this.error;
    }
  },
  watch: {
    show(newVal) {
      if (newVal) {
        this.newDancerEmail = '';
        this.localError = '';
      }
    }
  },
  methods: {
    handleConfirm() {
      this.localError = '';
      const trimmedNew = this.newDancerEmail.trim();
      if (trimmedNew && trimmedNew.toLowerCase() === (this.currentDancerEmail || '').trim().toLowerCase()) {
        this.localError = 'Az új táncos e-mail cím nem egyezhet meg az átadott szülői e-mail címmel.';
        return;
      }
      this.$emit('confirm', {
        newDancerEmail: trimmedNew
      });
    }
  }
};
</script>

<style scoped>
/* Modal Transition */
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.25s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}

.modal-fade-enter-active .modal-forfeit-card {
  animation: modalScaleIn 0.25s cubic-bezier(0.16, 1, 0.3, 1);
}

.modal-fade-leave-active .modal-forfeit-card {
  animation: modalScaleOut 0.2s ease-in;
}

@keyframes modalScaleIn {
  from {
    transform: scale(0.92) translateY(12px);
    opacity: 0;
  }
  to {
    transform: scale(1) translateY(0);
    opacity: 1;
  }
}

@keyframes modalScaleOut {
  from {
    transform: scale(1) translateY(0);
    opacity: 1;
  }
  to {
    transform: scale(0.94) translateY(8px);
    opacity: 0;
  }
}

/* Modal Overlay */
.mdl-overlay {
  position: fixed;
  inset: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(15, 23, 42, 0.72);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow-y: auto;
  padding: 1.5rem 1rem;
  z-index: 999999;
}

/* Modal Card */
.modal-forfeit-card {
  background: var(--card-bg, #ffffff) !important;
  color: var(--color-text, #1e293b) !important;
  border: 1px solid var(--color-border, #e2e8f0) !important;
  border-radius: 22px !important;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(255, 255, 255, 0.05) !important;
  max-width: 520px;
  width: 100%;
  position: relative;
  overflow: hidden;
  margin: auto;
}

.modal-accent-bar {
  height: 4px;
  width: 100%;
  background: linear-gradient(90deg, #6366f1, #3b82f6, #06b6d4);
}

/* Header */
.modal-header-section {
  border-bottom: 1px solid var(--color-border, #f1f5f9);
}

.modal-badge-icon {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: linear-gradient(135deg, rgba(99, 102, 241, 0.15), rgba(59, 130, 246, 0.2));
  color: #4f46e5;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  border: 1px solid rgba(99, 102, 241, 0.25);
}

.modal-title {
  color: var(--color-heading, #0f172a);
  font-size: 1.18rem;
  letter-spacing: -0.01em;
}

.modal-subtitle {
  color: var(--color-text-muted, #64748b);
  font-size: 0.85rem;
}

.modal-close-button {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  border: 1px solid var(--color-border, #e2e8f0);
  background: var(--color-background-soft, #f8fafc);
  color: var(--color-text-muted, #64748b);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
}

.modal-close-button:hover {
  background: var(--color-border, #e2e8f0);
  color: var(--color-heading, #0f172a);
  transform: rotate(90deg);
}

/* Transfer Visual Card */
.transfer-flow-card {
  background: linear-gradient(135deg, rgba(99, 102, 241, 0.07), rgba(59, 130, 246, 0.05));
  border: 1px solid rgba(99, 102, 241, 0.2);
}

.transfer-flow-labels {
  color: var(--color-text-muted, #64748b);
  font-size: 0.78rem;
}

.role-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  display: inline-block;
}

.role-dot-dancer {
  background-color: #f59e0b;
}

.role-dot-parent {
  background-color: #10b981;
}

.transfer-arrow {
  color: #6366f1;
  font-weight: bold;
}

.transfer-email-display {
  background: var(--card-bg, #ffffff);
  border: 1px solid rgba(99, 102, 241, 0.25);
  box-shadow: 0 2px 4px rgba(99, 102, 241, 0.06);
}

.transfer-mail-icon {
  font-size: 1.1rem;
}

.transfer-mail-text {
  color: var(--primary-blue, #2563eb);
  font-size: 0.95rem;
  word-break: break-all;
}

.transfer-badge {
  background: rgba(99, 102, 241, 0.12);
  color: #4338ca;
  font-size: 0.72rem;
  font-weight: 600;
  border: 1px solid rgba(99, 102, 241, 0.2);
}

.transfer-explainer {
  color: var(--color-text-muted, #64748b);
  line-height: 1.45;
  font-size: 0.83rem;
}

/* Inputs */
.input-label {
  color: var(--color-heading, #0f172a);
}

.optional-badge {
  background: var(--color-background-soft, #f1f5f9);
  color: var(--color-text-muted, #64748b);
  border: 1px solid var(--color-border, #e2e8f0);
  font-size: 0.72rem;
  font-weight: 500;
}

.input-wrapper {
  position: relative;
}

.input-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 1rem;
  pointer-events: none;
  opacity: 0.7;
}

.modern-input-styled {
  padding-left: 2.5rem;
  height: 42px;
  background-color: var(--card-bg, #ffffff);
  color: var(--color-text, #1e293b);
  border: 1.5px solid var(--color-border, #cbd5e1);
  border-radius: 10px;
  font-size: 0.92rem;
  transition: all 0.2s ease;
}

.modern-input-styled:focus {
  border-color: #6366f1;
  outline: none;
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.18);
}

/* Helper Box */
.helper-box {
  transition: all 0.2s ease;
  line-height: 1.4;
  border: 1px solid transparent;
}

.helper-box--default {
  background: var(--color-background-soft, #f8fafc);
  color: var(--color-text-muted, #64748b);
  border-color: var(--color-border, #e2e8f0);
}

.helper-box--active {
  background: rgba(59, 130, 246, 0.08);
  color: #1e40af;
  border-color: rgba(59, 130, 246, 0.25);
}

.helper-icon {
  font-size: 0.95rem;
  flex-shrink: 0;
  margin-top: 1px;
}

/* Error Alert */
.alert-error-box {
  background-color: #fef2f2;
  color: #991b1b;
  border: 1px solid #fecaca;
}

.error-icon {
  font-size: 1.1rem;
  flex-shrink: 0;
}

/* Footer */
.modal-footer-section {
  border-top: 1px solid var(--color-border, #f1f5f9);
}

.modal-cancel-btn {
  background: var(--color-background-soft, #f8fafc);
  color: var(--color-text-muted, #64748b);
  border: 1px solid var(--color-border, #e2e8f0);
  font-weight: 500;
  transition: all 0.2s ease;
}

.modal-cancel-btn:hover {
  background: var(--color-border, #e2e8f0);
  color: var(--color-heading, #0f172a);
}

/* Dark Mode Overrides */
[data-theme="dark"] .modal-forfeit-card {
  background: #1e293b !important;
  color: #f8fafc !important;
  border-color: #334155 !important;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.7), 0 0 0 1px rgba(255, 255, 255, 0.08) !important;
}

[data-theme="dark"] .modal-header-section {
  border-bottom-color: #334155 !important;
}

[data-theme="dark"] .modal-footer-section {
  border-top-color: #334155 !important;
}

[data-theme="dark"] .modal-title {
  color: #f8fafc !important;
}

[data-theme="dark"] .modal-subtitle {
  color: #94a3b8 !important;
}

[data-theme="dark"] .input-label {
  color: #e2e8f0 !important;
}

[data-theme="dark"] .modal-close-button {
  background: #334155 !important;
  border-color: #475569 !important;
  color: #cbd5e1 !important;
}

[data-theme="dark"] .modal-close-button:hover {
  background: #475569 !important;
  color: #ffffff !important;
}

[data-theme="dark"] .transfer-flow-card {
  background: linear-gradient(135deg, rgba(99, 102, 241, 0.16), rgba(59, 130, 246, 0.12)) !important;
  border-color: rgba(99, 102, 241, 0.35) !important;
}

[data-theme="dark"] .transfer-email-display {
  background: #0f172a !important;
  border-color: rgba(99, 102, 241, 0.4) !important;
}

[data-theme="dark"] .transfer-mail-text {
  color: #93c5fd !important;
}

[data-theme="dark"] .transfer-badge {
  background: rgba(99, 102, 241, 0.25) !important;
  color: #c7d2fe !important;
  border-color: rgba(99, 102, 241, 0.4) !important;
}

[data-theme="dark"] .transfer-explainer {
  color: #94a3b8 !important;
}

[data-theme="dark"] .transfer-flow-labels {
  color: #94a3b8 !important;
}

[data-theme="dark"] .modern-input-styled {
  background-color: #0f172a !important;
  color: #f8fafc !important;
  border-color: #334155 !important;
}

[data-theme="dark"] .modern-input-styled:focus {
  border-color: #818cf8 !important;
  box-shadow: 0 0 0 3px rgba(129, 140, 248, 0.25) !important;
}

[data-theme="dark"] .optional-badge {
  background: #334155 !important;
  border-color: #475569 !important;
  color: #94a3b8 !important;
}

[data-theme="dark"] .helper-box--default {
  background: rgba(15, 23, 42, 0.6) !important;
  color: #94a3b8 !important;
  border-color: #334155 !important;
}

[data-theme="dark"] .helper-box--active {
  background: rgba(30, 58, 138, 0.3) !important;
  color: #bfdbfe !important;
  border-color: rgba(96, 165, 250, 0.35) !important;
}

[data-theme="dark"] .alert-error-box {
  background-color: rgba(153, 27, 27, 0.3) !important;
  color: #fca5a5 !important;
  border-color: rgba(239, 68, 68, 0.4) !important;
}

[data-theme="dark"] .modal-cancel-btn {
  background: #334155 !important;
  border-color: #475569 !important;
  color: #cbd5e1 !important;
}

[data-theme="dark"] .modal-cancel-btn:hover {
  background: #475569 !important;
  color: #ffffff !important;
}
</style>
